import { CellContext } from '@tanstack/react-table'
import { ColumnDefWithFilters, EnrichedTeamMembership } from '../types'
import { useMemo } from 'react'
import { useAudit } from '../context'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { sum } from 'lodash'
import { Difficulty, Season } from 'app/frontend/types/custom'
import { Check, X, Medal } from 'lucide-react'
import {
  difficulties,
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import Select from 'app/frontend/base/components/Select'
import SeasonSelect from '../../TeamLoot/components/SeasonSelect'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { cn } from '@/lib/utils'
import { rankToColor } from '../utils'

const useRaidsColumns = (type: 'weekly' | 'overall'): ColumnDefWithFilters => {
  const { selectedSeason, setSelectedSeason, visibleMembers } = useAudit()
  const { value: difficulty, setValue: setDifficulty } =
    useAuditFilterState<Difficulty>('raidDifficulty', 'mythic', difficulties)
  const {
    data: {
      currentExpansion: { seasons },
    },
  } = usePageInfo()

  const offsetByInstance = useMemo(() => {
    return seasons
      .flatMap((season) => season.instances)
      .sort((a, b) => a.id - b.id)
      .reduce((acc, instance) => {
        acc[instance.id] = {
          amount: instance.encounters.length,
          offset: sum(Object.values(acc).map((o) => o.amount)),
        }
        return acc
      }, {} as Record<number, { amount: number; offset: number }>)
  }, [seasons])

  const getValueForIndex = (
    member: EnrichedTeamMembership,
    instance: wowaudit.Instance,
    encounterIndex: number,
  ) => {
    if (!offsetByInstance[instance.id]) return false

    const value =
      member.data?.[
        `raids_${difficulty}${type === 'weekly' ? '_weekly' : ''}`
      ]?.split('|')[offsetByInstance[instance.id].offset + encounterIndex]

    return type === 'weekly' ? value === '1' : value
  }

  const possibleAmount = useMemo(
    () =>
      selectedSeason.instances.reduce((acc, instance) => {
        return acc + instance.encounters.length
      }, 0),
    [selectedSeason],
  )

  const rankById = useMemo(() => {
    if (type === 'weekly' || !visibleMembers.some((row) => row.data)) {
      return undefined
    }

    const withTotals = visibleMembers.map((row) => ({
      ...row,
      total: selectedSeason.instances.reduce((acc, instance) => {
        return (
          acc +
          instance.encounters.reduce(
            (acc, _, i) =>
              acc + Number(getValueForIndex(row, instance, i) ?? '0'),
            0,
          )
        )
      }, 0),
    }))

    const sorted = [...withTotals].sort((a, b) => b.total - a.total)

    return Object.fromEntries(
      withTotals.map((row) => {
        const rank = sorted.findIndex((r) => r.total === row.total) + 1
        return [row.id, rank]
      }),
    )
  }, [difficulty, selectedSeason, visibleMembers, type])

  const columns = useMemo(() => {
    return [
      ...(type === 'weekly'
        ? []
        : [
            {
              id: 'rank',
              size: 75,
              accessorFn: (row: EnrichedTeamMembership) =>
                rankById?.[row.id.toString()] ?? 0,
              ...genericSorting<EnrichedTeamMembership>('alphanumeric', 'Rank'),
              cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
                const value = rankById?.[info.row.original.id.toString()]
                return value ? (
                  <div className={cn('flex items-center justify-center gap-2')}>
                    <Medal className={cn('w-4 h-4', rankToColor(value))} />{' '}
                    {value}
                  </div>
                ) : (
                  ''
                )
              },
            },
          ]),
      {
        id: `total_${difficulty}_${type}`,
        size: 100,
        meta: {
          className: 'border-r border-accent',
        },
        ...genericSorting<EnrichedTeamMembership>(
          'alphanumeric',
          'Total',
          type === 'overall',
        ),
        accessorFn: (row: EnrichedTeamMembership) => {
          return selectedSeason.instances.reduce((acc, instance) => {
            return (
              acc +
              instance.encounters.reduce((acc, _, i) => {
                return (
                  acc +
                  (type === 'weekly'
                    ? getValueForIndex(row, instance, i)
                      ? 1
                      : 0
                    : Number(getValueForIndex(row, instance, i) ?? '0'))
                )
              }, 0)
            )
          }, 0)
        },
        cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
          const killedAmount = info.getValue() as number

          return type === 'weekly' ? (
            <span>
              <span
                className={
                  killedAmount === possibleAmount
                    ? 'text-green-500'
                    : killedAmount === 0
                    ? 'text-red-500'
                    : 'text-yellow-500'
                }
              >
                {killedAmount}
              </span>{' '}
              / {possibleAmount}
            </span>
          ) : (
            <span>{info.getValue() as string}</span>
          )
        },
      },
      ...selectedSeason.instances.flatMap((instance) => ({
        id: `raid_${instance.id}_${difficulty}_${type}`,
        header: instance.name,
        columns: instance.encounters.map((encounter, i) => ({
          id: `raid_${instance.id}_${encounter.id}_${difficulty}_${type}`,
          ...genericSorting<EnrichedTeamMembership>(
            type === 'weekly' ? 'basic' : 'alphanumeric',
            encounter.name,
            true,
          ),
          accessorFn: (row: EnrichedTeamMembership) =>
            getValueForIndex(row, instance, i),
          cell: (info: CellContext<EnrichedTeamMembership, unknown>) =>
            type === 'weekly' ? (
              <div className='flex items-center justify-center'>
                {info.getValue() ? (
                  <Check className='w-4 h-4 text-green-500' />
                ) : (
                  <X className='w-4 h-4 text-red-500' />
                )}
              </div>
            ) : (
              <div
                className={cn(
                  'flex items-center justify-center',
                  info.getValue() === '0' ? 'opacity-30' : '',
                )}
              >
                {info.getValue() as string}
              </div>
            ),
        })),
      })),
    ]
  }, [difficulty, selectedSeason, possibleAmount, rankById])

  const filters = useMemo(() => {
    return [
      <AuditFilter key='season' label='Season'>
        <SeasonSelect
          className='w-52'
          value={selectedSeason.id.toString()}
          setValue={(value) =>
            setSelectedSeason(
              seasons.find((s) => s.id.toString() === value) as Season,
            )
          }
        />
      </AuditFilter>,
      <AuditFilter key='difficulty' label='Difficulty'>
        <Select
          className='w-32'
          setValue={(value) => setDifficulty(value as Difficulty)}
          value={difficulty}
          options={difficulties.map((diff) => ({
            label: difficultiesLabels[diff],
            value: diff,
          }))}
        />
      </AuditFilter>,
    ]
  }, [difficulty, selectedSeason, setSelectedSeason, seasons])

  return {
    columns,
    filters,
    initialSortBy: [
      { id: `total_${difficulty}_${type}`, desc: type === 'overall' },
    ],
  }
}

export const useRaidsOverallColumns = (): ColumnDefWithFilters => {
  return useRaidsColumns('overall')
}

export const useRaidsWeeklyColumns = (): ColumnDefWithFilters => {
  return useRaidsColumns('weekly')
}
